import { Tr, Td, ExpandableRowContent } from '@patternfly/react-table'
import * as React from 'react'
import { useCallback, useMemo } from 'react'

import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import {
  filterInteractionsByFeatureFlags,
  getStatusLabel,
} from '@app/domain/program/program.helpers'
import type {
  IProgramEnrolment} from '@app/domain/program/program.interface';
import {
  ProgramEnrolmentStatusId,
} from '@app/domain/program/program.interface'
import { formatIsoDateTimeOffset } from '@app/i18n/formats'
import { InfoTooltip } from '@app/utils/info-tooltip-icon'
import { ActionLink } from '@app/utils/styles/action-link'

import { PatientProgramInteractionList } from './interaction-list'

interface Props {
  programEnrolment: IProgramEnrolment
  onProgramSelected: (program: IProgramEnrolment) => void
  onSendInteractionToIntakeAnywhere: (programInteractionId: string) => void
  onRevokeInteraction: (programInteractionId: string) => void
  rowIndex: number
  isProgramExpanded: boolean
  setProgramExpanded: (
    program: IProgramEnrolment,
    isProgramExpanding: boolean,
  ) => void
}

interface StatusProps {
  status: ProgramEnrolmentStatusId
  statusReason: string
  statusReasonDescription: string
}

export const StatusLabel = ({
  status,
  statusReason,
  statusReasonDescription,
}: StatusProps) => {
  return (
    <>
      {' ( '}
      {getStatusLabel(status)}
      {(statusReason || statusReasonDescription) && ' - '}
      {statusReason}
      {statusReasonDescription && (
        <InfoTooltip content={statusReasonDescription} />
      )}
      {' ) '}
    </>
  )
}

export const ProgramListRow = ({
  programEnrolment,
  onProgramSelected,
  onSendInteractionToIntakeAnywhere,
  onRevokeInteraction,
  isProgramExpanded,
  setProgramExpanded,
  rowIndex,
}: Props) => {
  const { isFeatureFlagEnabled } = useFeatureFlags()

  const enabledFeatureFlags = useMemo(() => {
    return Object.values(FeatureFlagId).filter((flag) =>
      isFeatureFlagEnabled(flag),
    )
  }, [isFeatureFlagEnabled])

  const filteredPatientProgramInteractions = useMemo(() => {
    return filterInteractionsByFeatureFlags(
      programEnrolment.programInteractions || [],
      enabledFeatureFlags,
      (interaction) => interaction.interactionType,
    )
  }, [programEnrolment.programInteractions, enabledFeatureFlags])

  const handleProgramSelected = useCallback(() => {
    onProgramSelected(programEnrolment)
  }, [onProgramSelected, programEnrolment])

  const tableDataIsExpandable = useMemo(
    () => ({
      rowIndex,
      isExpanded: isProgramExpanded,
      onToggle: () => setProgramExpanded(programEnrolment, !isProgramExpanded),
    }),
    [rowIndex, isProgramExpanded, programEnrolment, setProgramExpanded],
  )

  return (
    <>
      <Tr isHoverable>
        <Td
          expand={
            programEnrolment.program.interactions
              ? tableDataIsExpandable
              : undefined
          }
        />
        <Td>
          <ActionLink onClick={handleProgramSelected}>
            {programEnrolment.program.name}
            {(programEnrolment.status ===
              ProgramEnrolmentStatusId.Discontinued ||
              programEnrolment.status === ProgramEnrolmentStatusId.Removed) && (
              <>
                <StatusLabel
                  status={programEnrolment.status}
                  statusReason={
                    programEnrolment.status === ProgramEnrolmentStatusId.Removed
                      ? undefined
                      : programEnrolment.statusReason
                  }
                  statusReasonDescription={
                    programEnrolment.status === ProgramEnrolmentStatusId.Removed
                      ? undefined
                      : programEnrolment.statusReasonDescription
                  }
                />
              </>
            )}
          </ActionLink>
        </Td>
      </Tr>

      {filteredPatientProgramInteractions?.length > 0 && (
        <Tr isExpanded={isProgramExpanded}>
          <Td colSpan={5}>
            <ExpandableRowContent>
              <div
                style={{
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  marginLeft: '1rem',
                }}
              >
                Start Date:{' '}
                <span style={{ fontWeight: 'normal' }}>
                  {formatIsoDateTimeOffset(programEnrolment.startDate)}
                </span>
              </div>
              <PatientProgramInteractionList
                programEnrolmentStatus={programEnrolment.status}
                patientProgramInteractions={filteredPatientProgramInteractions}
                onRevokeInteraction={onRevokeInteraction}
                onSendInteractionToIntakeAnywhere={
                  onSendInteractionToIntakeAnywhere
                }
                consentsToEmail={programEnrolment.consentsToEmail}
                consentsToSms={programEnrolment.consentsToSms}
              />
            </ExpandableRowContent>
          </Td>
        </Tr>
      )}
    </>
  )
}
